import { useTranslation } from 'react-i18next';

/**
 * Custom hook that provides translation function with built-in fallback support
 * Usage: const t = useTranslationWithFallback();
 * Then: t('materialSubstitution.title', 'Material Substitution')
 *
 * This hook provides several advantages:
 * 1. Cleaner syntax: t('key', 'fallback') instead of t('key', { defaultValue: 'fallback' })
 * 2. Automatic fallback when translation fails or returns the key itself
 * 3. Error handling for i18n failures
 * 4. TypeScript support
 */
export const useTranslationWithFallback = () => {
  const { t: originalT, ready } = useTranslation();

  const t = (key: string, fallback?: string): string => {
    try {
      // If i18n is not ready yet, use fallback
      if (!ready && fallback) {
        return fallback;
      }

      const translation = originalT(key, { defaultValue: fallback });

      // If translation returns the key itself (indicating missing translation), use fallback
      if (translation === key && fallback) {
        return fallback;
      }

      return translation;
    } catch (error) {
      console.warn(`Translation failed for key "${key}":`, error);
      // If there's any error with translation, use fallback or key
      return fallback || key;
    }
  };

  return t;
};
